import type { ChatMessageGroup } from '@provision/chat-utils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  useChatPagination,
  useMergedTimeline,
} from 'src/pages/Conversations/Chat/registry/hooks';
import { MessageTypeId, TimelineEntry } from 'src/pages/Conversations/Chat/registry/types';
import { useNotifications } from 'src/utils/hooks';
import {
  DeepLinkPhase,
  MessageDeepLinkContext,
  MessageDeepLinkState,
} from './message-deep-link-context';
import { useCenteredRowAnchor } from './use-centered-row-anchor';
import { useMessageDeepLinkParam } from './use-message-deep-link-param';
import {
  TargetLookupStatus,
  TargetMessageLookup,
  useTargetMessageLookup,
} from './use-target-message-lookup';

// A safety stop for a pagination signal that never turns false, not a functional limit:
// the target's createdAt ends the paging long before this in practice.
export const MAX_DEEP_LINK_PAGES = 100;

interface PageRequests {
  targetMessageId: string;
  count: number;
  requestedWith: () => Promise<void>;
}

interface RenderedMessages {
  ids: Set<string>;
  oldestMs: number;
}

function collectRenderedMessages(entries: TimelineEntry<unknown>[]): RenderedMessages {
  const ids = new Set<string>();
  let oldestMs = Infinity;
  entries.forEach((entry) => {
    if (entry.type !== MessageTypeId.ChatMessage) return;
    (entry.item as ChatMessageGroup<unknown>).messages.forEach((message) => {
      ids.add(message.id);
      const ms = Date.parse(message.createdAt);
      if (ms < oldestMs) oldestMs = ms;
    });
  });
  return { ids, oldestMs };
}

interface PhaseInput {
  targetMessageId: string | null;
  lookup: TargetMessageLookup;
  rendered: RenderedMessages;
  hasMore: boolean;
  isPaging: boolean;
  pagesRequested: number;
}

function resolvePhase({
  targetMessageId,
  lookup,
  rendered,
  hasMore,
  isPaging,
  pagesRequested,
}: PhaseInput): DeepLinkPhase {
  if (!targetMessageId) return DeepLinkPhase.Idle;
  if (rendered.ids.has(targetMessageId)) return DeepLinkPhase.Found;
  if (lookup.status === TargetLookupStatus.Missing) return DeepLinkPhase.NotFound;
  // before the first page lands `hasMore` reads false (an "unfilled" page of zero records)
  if (lookup.status !== TargetLookupStatus.Found || isPaging || rendered.ids.size === 0) {
    return DeepLinkPhase.Locating;
  }

  /*
    The loaded window already reaches past the target, yet it is not on screen: the row is
    hidden (an SMS session folds its messages away), so no further page can bring it.
  */
  const isWindowPastTarget = rendered.oldestMs < Date.parse(lookup.createdAt ?? '');
  if (!hasMore || isWindowPastTarget || pagesRequested >= MAX_DEEP_LINK_PAGES) {
    return DeepLinkPhase.NotFound;
  }
  return DeepLinkPhase.Locating;
}

/*
  Brings the message named by `?messageId` into the thread, centres it and highlights it
  until the reader's first gesture. Mounted per conversation (Chat is keyed by it), so
  switching conversations starts from a clean state.
*/
export const MessageDeepLinkProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const conversationId = useChatCommonData((s) => s.conversationId);
  const chatRef = useChatCommonData((s) => s.chatRef);
  const gestureClock = useChatCommonData((s) => s.gestureClock);
  const scrollGuard = useChatCommonData((s) => s.scrollGuard);
  const { showErrorAlert } = useNotifications();
  const anchorRow = useCenteredRowAnchor(chatRef, scrollGuard, gestureClock);

  const { targetMessageId, clearTargetParam } = useMessageDeepLinkParam();
  const lookup = useTargetMessageLookup(conversationId, targetMessageId);
  const entries = useMergedTimeline();
  const { hasMore, isLoadingMore, loadMore } = useChatPagination();

  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [pageRequests, setPageRequests] = useState<PageRequests>();
  const isCurrentTarget = pageRequests?.targetMessageId === targetMessageId;
  const pagesRequested = isCurrentTarget ? pageRequests.count : 0;

  /*
    `loadMore` closes over the loaded offset, so it is replaced exactly when a requested
    page has merged into the thread. Until then the previous request is still in flight:
    its promise settles on the cache write, before the thread re-renders with the page, and
    requesting again at that point would load one page past the target.
  */
  const isAwaitingMerge = isCurrentTarget && pageRequests.requestedWith === loadMore;

  const rendered = useMemo(() => collectRenderedMessages(entries), [entries]);

  const isPaging = isLoadingMore || isAwaitingMerge;
  const phase = resolvePhase({
    targetMessageId,
    lookup,
    rendered,
    hasMore,
    isPaging,
    pagesRequested,
  });

  // the first page has to be on screen before paging: until then loadMore is a no-op
  const shouldRequestPage =
    phase === DeepLinkPhase.Locating &&
    lookup.status === TargetLookupStatus.Found &&
    !isPaging &&
    rendered.ids.size > 0;

  /*
    Deliberately ungated by the scroll gesture gate: this chain is the one place the thread
    pages without a reader gesture, bounded by the target's createdAt and the page cap.
  */
  useEffect(() => {
    if (!shouldRequestPage || !targetMessageId) return;
    setPageRequests((prev) => ({
      targetMessageId,
      count: prev?.targetMessageId === targetMessageId ? prev.count + 1 : 1,
      requestedWith: loadMore,
    }));
    void loadMore();
  }, [shouldRequestPage, targetMessageId, loadMore]);

  /*
    A ref, not state: it only stops a resolved target from being acted on twice while the
    param removal is still propagating through the router, and must not re-render.
  */
  const resolvedTargetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!targetMessageId) {
      resolvedTargetRef.current = null;
      return;
    }
    if (resolvedTargetRef.current === targetMessageId) return;

    if (phase === DeepLinkPhase.Found) {
      resolvedTargetRef.current = targetMessageId;
      anchorRow(targetMessageId);
      setHighlightedMessageId(targetMessageId);
      clearTargetParam();
    } else if (phase === DeepLinkPhase.NotFound) {
      resolvedTargetRef.current = targetMessageId;
      showErrorAlert('Message not found in this conversation');
      clearTargetParam();
    }
  }, [phase, targetMessageId, anchorRow, clearTargetParam, showErrorAlert]);

  const clearHighlight = useCallback(() => setHighlightedMessageId(null), []);

  useEffect(() => {
    if (!highlightedMessageId) return;

    const unsubscribe = gestureClock.subscribe(clearHighlight);
    // capture: a click that a child stops from bubbling still counts as interaction
    document.addEventListener('click', clearHighlight, true);
    return () => {
      unsubscribe();
      document.removeEventListener('click', clearHighlight, true);
    };
  }, [highlightedMessageId, gestureClock, clearHighlight]);

  const value = useMemo<MessageDeepLinkState>(
    () => ({ targetMessageId, phase, highlightedMessageId }),
    [targetMessageId, phase, highlightedMessageId],
  );

  return (
    <MessageDeepLinkContext.Provider value={value}>{children}</MessageDeepLinkContext.Provider>
  );
};
